/**
 * Row model + sorting for the pipeline Table renderer (SE-5491).
 * @remarks
 * Pure helpers, deliberately free of React so the dual-view consistency
 * invariant (AC1) and the unstaged-bucket invariant (AC2) can be asserted
 * directly in unit tests.
 *
 * Lives in `features/player-kanban/utils` (not next to the component) because
 * component directories are restricted to index/Container/View files by the
 * project's component-structure lint rule.
 * @module features/player-kanban/utils/pipelineTableRows
 */
import type { ColumnData } from "../types/types";

/** A tag on a row — carries `id` because the remove mutation keys on it. */
export interface PipelineTableTag {
  /** Tag id, the argument `removeTagsFromPlayer` takes. */
  readonly id: string;
  /** Display name. */
  readonly name: string;
}

/** A watchlist the player belongs to (the table's "List" column). */
export interface PipelineTableWatchlist {
  /** Watchlist id. */
  readonly id: string;
  /** Watchlist display name, e.g. "High Potentials". */
  readonly name: string;
}

/** One player, in one stage — the Table renderer's unit of display. */
export interface PipelineTableRow {
  /** Player id — the card's identity, shared with the Kanban renderer. */
  readonly playerId: string;
  /** Phase (stage) id the player currently sits in. */
  readonly stageId: string;
  /** Phase (stage) display name. */
  readonly stageName: string;
  /**
   * 1-based rank within the stage.
   * @remarks
   * Derived exactly as `KanbanColumnContainer` derives it: the player's own
   * `rank` when present, else the ordinal over `columnsData[].players`.
   *
   * SE-5493 made that first branch load-bearing. On a PIPELINE board
   * `generateColumnsData` stamps the viewer's server-resolved `rank` onto each
   * player and orders the stage by it, so the Table shows the same number the
   * Kanban badge shows. Before that, `columnsData` players carried no `rank`
   * and this always fell through to `index + 1` — harmless while the Kanban
   * was ALSO a pure ordinal, but the moment the Kanban started reading the
   * viewer's private order the two views would have disagreed and broken the
   * dual-view set-consistency invariant (SE-5491 AC1).
   *
   * The ordinal fallback still covers ROSTER/WATCHLIST boards (no per-user
   * ordering exists there) and the synthetic unstaged bucket, which
   * deliberately carries no rank because its members span different stages.
   */
  readonly rank: number;
  readonly playerName: string;
  readonly age?: number;
  readonly gpr?: number;
  /** Club, or the free-agent placeholder the board derivation stamps. */
  readonly teamName: string;
  /**
   * Country of the club's league.
   * @remarks
   * Live: `ListKanbanCards` selects `bioData.leagueCountry`. A country NAME.
   * DORMANT since SE-6872 removed the Club cell's flag-emoji prefix (it read
   * as a broken club-icon placeholder — Sprint 18 staging QA); kept on the
   * model, like `stage` while its column was out, so reintroducing club
   * iconography is a definition rather than a re-derivation.
   */
  readonly clubCountry?: string | null;
  /**
   * SE-6554 (Figma 80:15170) — the columns the design's table carries beyond
   * identity. Every one of these already rides on the board's `columnsData`
   * players, so the Table reads the SAME projection the Kanban cards do and
   * needs no query of its own (the AC1 invariant this model exists to hold).
   */
  readonly position?: string;
  /** Months left on the contract; `null` when the card carries no contract. */
  readonly contractMonths: number | null;
  /** Backing card's last-update timestamp, driving the Activity column. */
  readonly updatedAt?: string | null;
  /** Server-side total of scout reports attached to the player. */
  readonly reportsCount?: number;
  /**
   * Latest report's overall SCORE, not a categorical verdict — see the
   * rendering note in PipelineTableView.
   */
  readonly verdict?: string | null;
  /** Board-scoped tags on the backing card. */
  readonly tags: readonly PipelineTableTag[];
  /**
   * Watchlists the player sits on, driving the "List" column.
   * @remarks
   * Live data since 2026-08-07 — `ListKanbanCards` selects `watchlists`. A
   * player on no watchlist arrives as an empty array, and the cell renders
   * blank rather than a placeholder (Raniel's ruling: never "- None").
   */
  readonly watchlists: readonly PipelineTableWatchlist[];
}

/**
 * Sortable column keys. `null` means the natural stage-then-rank order.
 * @remarks
 * Every column the table draws a sort chevron on is here, which is all of
 * them. The previous cut showed the chevron on five columns and nothing on the
 * rest; the design puts one on every header, and an affordance that renders
 * but does nothing is worse than no affordance, so the column set grew to meet
 * the design rather than the design being trimmed to the column set.
 *
 * `stage` outlived its dropped column (2026-08-07, Raniel's authoritative
 * column set — see the history block in `PipelineTableView`) as a dormant
 * comparator, kept so restoration would be a definition rather than a
 * re-derivation. SE-6870 proved that out: the Pipeline column now RENDERS the
 * stage name and its header sorts on `stage`, so the dormant entry is live
 * again. `pipeline` takes over as the dormant one — its cell no longer
 * reports bare membership, and its comparator ordered a constant anyway.
 */
export type PipelineTableSortKey =
  | "stage"
  | "rank"
  | "name"
  | "age"
  | "gpr"
  | "team"
  | "position"
  | "tags"
  | "contractMonths"
  | "activity"
  | "watchlists"
  | "pipeline"
  | "reports"
  | "verdict";

/** Sort direction of the active column. */
export type PipelineTableSortDirection = "asc" | "desc";

/**
 * Flatten the board's columns into table rows, preserving stage order and
 * per-stage card order.
 * @param columnsData - The board's columns, straight from `useKanbanBoard`
 * @returns One row per player per stage, in natural stage-then-rank order
 */
export const buildPipelineTableRows = (
  columnsData: ColumnData[]
): PipelineTableRow[] =>
  columnsData.flatMap(column =>
    column.players.map((player, index) => ({
      playerId: player.id,
      stageId: column.id,
      stageName: column.title,
      rank: player.rank ?? index + 1,
      playerName: player.name,
      age: player.age,
      gpr: player.gpr,
      teamName: player.teamName,
      clubCountry: player.clubCountry,
      position: player.position,
      contractMonths: player.contractMonths,
      updatedAt: player.updatedAt,
      reportsCount: player.reportsCount,
      verdict: player.verdict,
      tags: player.tags ?? [],
      watchlists: player.watchlists ?? [],
    }))
  );

/**
 * Compare two rows on a numeric field, sorting missing values last.
 * @param a - Left value
 * @param b - Right value
 * @returns Negative, zero, or positive ordering result
 */
const compareNumeric = (a?: number, b?: number): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
};

/**
 * Compare two rows on a text field, sorting blanks last.
 * @param a - Left value
 * @param b - Right value
 * @returns Negative, zero, or positive ordering result
 */
const compareText = (a?: string | null, b?: string | null): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
};

/**
 * The latest report's score as a number, for the Verdict column's ordering.
 * @param row - Row being compared
 * @returns The parsed score, or undefined when the row carries none
 */
const verdictScore = (row: PipelineTableRow): number | undefined => {
  if (row.verdict == null) return undefined;
  const parsed = Number.parseFloat(row.verdict);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Card activity as an epoch millisecond value, for the Activity ordering.
 * @param row - Row being compared
 * @returns Milliseconds since the epoch, or undefined when never updated
 */
const activityAt = (row: PipelineTableRow): number | undefined => {
  if (!row.updatedAt) return undefined;
  const parsed = Date.parse(row.updatedAt);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Sort key → ascending comparator.
 * @remarks
 * A lookup table rather than a `switch`, so adding the design's remaining
 * sortable columns costs one entry each and the function keeps a cognitive
 * complexity of one (the project's `sonarjs/cognitive-complexity` budget).
 */
const COMPARATORS: Record<
  PipelineTableSortKey,
  (a: PipelineTableRow, b: PipelineTableRow) => number
> = {
  stage: (a, b) => a.stageName.localeCompare(b.stageName) || a.rank - b.rank,
  rank: (a, b) => a.rank - b.rank,
  name: (a, b) => a.playerName.localeCompare(b.playerName),
  team: (a, b) => compareText(a.teamName, b.teamName),
  age: (a, b) => compareNumeric(a.age, b.age),
  gpr: (a, b) => compareNumeric(a.gpr, b.gpr),
  position: (a, b) => compareText(a.position, b.position),
  tags: (a, b) => a.tags.length - b.tags.length,
  contractMonths: (a, b) =>
    compareNumeric(
      a.contractMonths ?? undefined,
      b.contractMonths ?? undefined
    ),
  activity: (a, b) => compareNumeric(activityAt(a), activityAt(b)),
  watchlists: (a, b) => a.watchlists.length - b.watchlists.length,
  // DORMANT since SE-6870 pointed the Pipeline header at `stage` (the cell
  // shows the stage name now). Kept, exactly as `stage` was kept while its
  // column was out: it ordered a constant anyway — every row of a pipeline
  // board is in THIS pipeline — and the ordering only becomes real if
  // cross-pipeline membership ever becomes queryable.
  pipeline: () => 0,
  reports: (a, b) => compareNumeric(a.reportsCount, b.reportsCount),
  verdict: (a, b) => compareNumeric(verdictScore(a), verdictScore(b)),
};

/**
 * Apply the active sort to the rows.
 * @remarks
 * Non-mutating: sorting a view of the board must never reorder the dataset the
 * Kanban renderer reads (that would break dual-view consistency, AC1).
 * @param rows - Rows in natural stage-then-rank order
 * @param key - Active sort column, or null for the natural order
 * @param direction - Active sort direction
 * @returns A new, sorted array
 */
export const sortPipelineTableRows = (
  rows: PipelineTableRow[],
  key: PipelineTableSortKey | null,
  direction: PipelineTableSortDirection
): PipelineTableRow[] => {
  if (!key) return rows;
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => factor * COMPARATORS[key](a, b));
};
